import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Leaf } from 'lucide-react';
import { useLanguage, useT, LOCALES } from '../lib/i18n';
import { useAuth } from '../lib/AuthContext';
import { useCart } from '../hooks/useCart';
import { CartBadge, LanguageSwitcher, AuthMenu } from './nav/NavWidgets';
import MobileDrawer from './nav/MobileDrawer';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { locale, setLocale } = useLanguage();
  const t = useT();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { totalItems } = useCart();

  const isActive = (href) => location.pathname.startsWith(href);

  return (
    <header data-testid="navbar" className="sticky top-0 z-50 bg-background/70 backdrop-blur-xl backdrop-saturate-150 border-b border-border/50 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center transition-transform group-hover:scale-110">
              <Leaf className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-serif text-xl font-medium tracking-wide text-foreground">The Herborist</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/catalog" data-testid="nav-catalog"
              className={`text-sm font-medium transition-colors hover:text-primary ${isActive('/catalog') ? 'text-primary' : 'text-muted-foreground'}`}>
              {t.nav.catalog}
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin/products" data-testid="nav-admin"
                className={`text-sm font-medium transition-colors hover:text-primary ${isActive('/admin') ? 'text-primary' : 'text-muted-foreground'}`}>
                {t.nav.admin}
              </Link>
            )}
            {user?.role === 'admin' && (
              <Link to="/ops" data-testid="nav-ops"
                className={`text-sm font-medium transition-colors hover:text-primary ${isActive('/ops') ? 'text-primary' : 'text-muted-foreground'}`}>
                Ops
              </Link>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <CartBadge totalItems={totalItems} />
            <LanguageSwitcher locale={locale} setLocale={setLocale} locales={LOCALES} />
            <AuthMenu user={user} logout={logout} t={t} />
          </div>

          <button className="md:hidden p-2" onClick={() => setOpen(o => !o)} aria-label="Menu" data-testid="mobile-menu-btn">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <MobileDrawer open={open} setOpen={setOpen} user={user} logout={logout} t={t}
        locale={locale} setLocale={setLocale} locales={LOCALES} totalItems={totalItems} />
    </header>
  );
}
